import type { TourStep } from '@klonup/design-system';

/**
 * Pasos compartidos del tramo "crear una tarea" del panel de creación (ConnectionTaskSplitPanel).
 * Los reutilizan el tour del BackupManager (bm-connection-detail) y el tour del Admin
 * (admin-create-task): el flujo de crear tarea es idéntico para ambos roles, así que
 * mantener un único origen evita que diverjan (DRY).
 *
 * El primer paso (`new-task`) usa `advanceOn` para avanzar cuando el usuario pulsa de verdad
 * el botón "Nueva tarea" → abre el panel. Las anclas (`task-name`, `task-config`,
 * `task-retention`, …) viven en ConnectionTaskSplitPanel y existen igual para Admin y BM (misma
 * página, mismo componente). El nombre vive siempre en el header del panel (`task-name`), por
 * eso su paso precede al de `config` (frecuencia, hora y estado).
 *
 * @param t          Función de traducción ya resuelta (react-i18next).
 * @param i18nPrefix Prefijo de claves i18n del tour que consume estos pasos, p.ej.
 *                   'onboarding.bm_connection_detail.steps' o 'onboarding.admin_create_task.steps'.
 *                   Cada tour mantiene sus propios textos bajo su prefijo.
 */
export function createTaskSteps(t: (key: string) => string, i18nPrefix: string): TourStep[] {
  return [
    {
      id: 'new-task',
      target: '[data-tour="connection-new-task-button"]',
      // Avanza cuando el usuario pulsa de verdad "Nueva tarea" → abre el panel de creación.
      advanceOn: { on: 'click' },
      title: t(`${i18nPrefix}.new_task.title`),
      description: t(`${i18nPrefix}.new_task.description`),
    },
    {
      id: 'name',
      target: '[data-tour="task-name"]',
      title: t(`${i18nPrefix}.name.title`),
      description: t(`${i18nPrefix}.name.description`),
    },
    {
      id: 'config',
      target: '[data-tour="task-config"]',
      title: t(`${i18nPrefix}.config.title`),
      description: t(`${i18nPrefix}.config.description`),
    },
    {
      id: 'retention',
      target: '[data-tour="task-retention"]',
      title: t(`${i18nPrefix}.retention.title`),
      description: t(`${i18nPrefix}.retention.description`),
    },
    {
      id: 'workspaces',
      target: '[data-tour="task-workspaces"]',
      title: t(`${i18nPrefix}.workspaces.title`),
      description: t(`${i18nPrefix}.workspaces.description`),
    },
    {
      id: 'notifications',
      target: '[data-tour="task-notifications"]',
      title: t(`${i18nPrefix}.notifications.title`),
      description: t(`${i18nPrefix}.notifications.description`),
    },
    {
      id: 'inclusions',
      target: '[data-tour="task-inclusions"]',
      title: t(`${i18nPrefix}.inclusions.title`),
      description: t(`${i18nPrefix}.inclusions.description`),
    },
    {
      id: 'done',
      // Sin target → modal centrado de cierre.
      title: t(`${i18nPrefix}.done.title`),
      description: t(`${i18nPrefix}.done.description`),
    },
  ];
}
